import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from '../router';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { Add, Upload, Headphones } from '@mui/icons-material';
import axios from 'axios';
import ColorModeToggle from '../components/ColorModeToggle';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function CartwallWindow() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const stationId = searchParams.get('stationId') || '';

  const [cartwall, setCartwall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [libraryDialogOpen, setLibraryDialogOpen] = useState(false);
  const [allAssets, setAllAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cartSearch, setCartSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [libraryLoading, setLibraryLoading] = useState(false);

  const [outputDevices, setOutputDevices] = useState([]);
  const [selectedPreviewOutput, setSelectedPreviewOutput] = useState('');
  const [supportsSinkId, setSupportsSinkId] = useState(false);

  const [playingPosition, setPlayingPosition] = useState(null);
  const [playingRemaining, setPlayingRemaining] = useState(0);
  const [previewPlayingPosition, setPreviewPlayingPosition] = useState(null);

  const previewAudioRef = useRef(null);
  const previewPlayingPositionRef = useRef(null);

  const withCredentials = { withCredentials: true };

  const fetchCartwall = async () => {
    if (!id) return;
    try {
      const res = await axios.get(`${API_URL}/api/cartwalls/${id}`, withCredentials);
      setCartwall(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load cartwall');
      setCartwall(null);
    }
  };

  useEffect(() => {
    if (!id || id === 'undefined') {
      setError(id === 'undefined' ? 'Invalid cartwall. Open from the Live page by selecting a cartwall and clicking "Open in New Window".' : 'No cartwall selected');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    fetchCartwall().finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    setSupportsSinkId(typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype);
    const enumerateDevices = async () => {
      let permissionStream = null;
      try {
        permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (_e) { /* labels may be unavailable until permission granted */ }
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const outputs = devices.filter((d) => d.kind === 'audiooutput');
        setOutputDevices(outputs);
        if (!selectedPreviewOutput && outputs[0]) setSelectedPreviewOutput(outputs[0].deviceId);
      } finally {
        permissionStream?.getTracks().forEach((track) => track.stop());
      }
    };
    enumerateDevices().catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current.src = '';
      }
    };
  }, []);

  const fetchAssetsAndCategories = async () => {
    setLibraryLoading(true);
    try {
      // Fetch all assets (no station filter) so library is visible
      const [assetsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/assets`, withCredentials),
        axios.get(`${API_URL}/api/categories`, {
          ...withCredentials,
          params: stationId ? { station_id: stationId } : {}
        })
      ]);
      const assets = assetsRes.data || [];
      const cats = categoriesRes.data || [];
      setAllAssets(Array.isArray(assets) ? assets : []);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (err) {
      setStatusMessage(`Failed to load library: ${err.response?.data?.message || err.message}`);
      setAllAssets([]);
      setCategories([]);
    } finally {
      setLibraryLoading(false);
    }
  };

  const startCountdown = (position, durationSeconds) => {
    const duration = Math.max(0, Math.floor(durationSeconds || 0));
    setPlayingPosition(position);
    setPlayingRemaining(duration);
  };

  const stopCountdown = () => {
    setPlayingPosition(null);
    setPlayingRemaining(0);
  };

  useEffect(() => {
    if (playingPosition == null) return;
    const interval = setInterval(() => {
      setPlayingRemaining((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          setPlayingPosition(null);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [playingPosition]);

  const playCart = async (assetId, position, durationSeconds) => {
    if (!assetId || !stationId) {
      setStatusMessage('Station not set. Open from Live page with a station selected.');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/live/cart/play`,
        { station_id: stationId, asset_id: assetId },
        withCredentials
      );
      setStatusMessage('Cart fired to playout.');
      setTimeout(() => setStatusMessage(''), 2000);
      startCountdown(position, durationSeconds);
    } catch (err) {
      setStatusMessage(`Cart play failed: ${err.response?.data?.message || err.message}`);
    }
  };

  const stopPreview = () => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.src = '';
      previewAudioRef.current = null;
    }
    previewPlayingPositionRef.current = null;
    setPreviewPlayingPosition(null);
    stopCountdown();
  };

  const handlePreviewClick = (assetId, position, durationSeconds) => {
    if (!assetId) return;
    if (previewPlayingPosition === position) {
      stopPreview();
      return;
    }
    playPreview(assetId, position, durationSeconds);
  };

  const playPreview = async (assetId, position, durationSeconds) => {
    if (!assetId) return;
    if (!supportsSinkId || !selectedPreviewOutput) {
      setStatusMessage('Select a preview output device above.');
      return;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.src = '';
      previewAudioRef.current = null;
    }
    const audio = new Audio();
    previewAudioRef.current = audio;
    const streamUrl = `${API_URL}/api/assets/${assetId}/stream`;
    audio.crossOrigin = 'anonymous';
    audio.preload = 'auto';
    try {
      await audio.setSinkId(selectedPreviewOutput);
    } catch (_e) { /* ignore sinkId errors */ }
    audio.src = streamUrl;
    audio.play().catch(() => {});
    previewPlayingPositionRef.current = position;
    setPreviewPlayingPosition(position);
    startCountdown(position, durationSeconds);
    audio.onended = () => {
      if (previewAudioRef.current !== audio) return;
      previewAudioRef.current = null;
      previewPlayingPositionRef.current = null;
      setPreviewPlayingPosition(null);
      stopCountdown();
    };
  };

  const addAssetToCartwall = async (asset) => {
    if (!id) return;
    try {
      await axios.post(
        `${API_URL}/api/cartwalls/${id}/items`,
        { asset_id: asset.id },
        withCredentials
      );
      setLibraryDialogOpen(false);
      setCartSearch('');
      setSelectedCategory('');
      setStatusMessage(`Added "${asset.title}" to cartwall.`);
      setTimeout(() => setStatusMessage(''), 2000);
      fetchCartwall();
    } catch (err) {
      setStatusMessage(`Failed to add: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target?.files;
    if (!files?.length || !id || !stationId) return;
    const file = files[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('station_id', stationId);
    formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
    try {
      const res = await axios.post(`${API_URL}/api/assets`, formData, { ...withCredentials });
      const asset = Array.isArray(res.data) ? res.data[0] : res.data;
      if (asset?.id) {
        await axios.post(
          `${API_URL}/api/cartwalls/${id}/items`,
          { asset_id: asset.id },
          withCredentials
        );
        setStatusMessage(`Uploaded and added "${asset.title}" to cartwall.`);
        setTimeout(() => setStatusMessage(''), 2000);
        fetchCartwall();
      }
    } catch (err) {
      setStatusMessage(`Upload failed: ${err.response?.data?.message || err.message}`);
    }
    e.target.value = '';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 'var(--rs-vh)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'var(--rs-bg)',
          color: 'var(--rs-text)'
        }}
      >
        <CircularProgress sx={{ color: '#22d3ee' }} />
      </Box>
    );
  }

  if (error || !cartwall) {
    return (
      <Box
        sx={{
          minHeight: 'var(--rs-vh)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'var(--rs-bg)',
          color: 'var(--rs-text)',
          p: 3
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const cols = cartwall.columns_count || 4;
  const rows = cartwall.rows_count || 6;
  const totalSlots = cols * rows;
  const items = (cartwall.items || []).reduce((acc, item) => {
    acc[item.position] = { ...item, index: item.position };
    return acc;
  }, {});

  const filteredAssets = allAssets.filter((asset) => {
    const matchesSearch = !cartSearch ||
      (asset.title || '').toLowerCase().includes(cartSearch.toLowerCase()) ||
      (asset.artist || '').toLowerCase().includes(cartSearch.toLowerCase());
    const matchesCategory = !selectedCategory || selectedCategory === '' ||
      (asset.categories || []).some(cat => String(cat.id) === String(selectedCategory)) ||
      String(asset.categoryId) === String(selectedCategory);
    return matchesSearch && matchesCategory;
  });

  return (
    <Box
      sx={{
        minHeight: 'var(--rs-vh)',
        bgcolor: 'var(--rs-bg)',
        color: 'var(--rs-text)',
        p: 2,
        boxSizing: 'border-box'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" sx={{ color: '#22d3ee', fontWeight: 600 }}>
          {cartwall.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ColorModeToggle placement="bottom" />
          {supportsSinkId && outputDevices.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel sx={{ color: '#999' }}>Preview output</InputLabel>
              <Select
                value={selectedPreviewOutput}
                label="Preview output"
                onChange={(e) => setSelectedPreviewOutput(e.target.value)}
                sx={{ color: 'var(--rs-text)' }}
              >
                {outputDevices.map((d) => (
                  <MenuItem key={d.deviceId} value={d.deviceId}>
                    {d.label || `Output ${d.deviceId.slice(0, 8)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            onClick={() => {
              setCartSearch('');
              setSelectedCategory('');
              fetchAssetsAndCategories();
              setLibraryDialogOpen(true);
            }}
            sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
          >
            Add from Library
          </Button>
          <Button
            size="small"
            variant="outlined"
            component="label"
            startIcon={<Upload />}
            sx={{ borderColor: '#22d3ee', color: '#22d3ee' }}
          >
            Upload
            <input type="file" hidden accept="audio/*" onChange={handleUpload} />
          </Button>
          {statusMessage && (
            <Typography variant="body2" sx={{ color: '#4caf50' }}>
              {statusMessage}
            </Typography>
          )}
        </Box>
      </Box>

      <Grid container spacing={1} sx={{ maxWidth: '100%' }}>
        {Array.from({ length: totalSlots }, (_, pos) => {
          const item = items[pos];
          const hasAsset = item && item.asset_id;
          const duration = item?.duration ?? 0;
          const isPlaying = playingPosition === pos;
          return (
            <Grid item xs={12 / cols} key={pos} sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={!hasAsset}
                  sx={{
                    justifyContent: 'flex-start',
                    minHeight: 64,
                    bgcolor: isPlaying ? '#00796b' : (hasAsset ? '#00695c' : 'var(--rs-paper)'),
                    color: hasAsset ? '#fff' : '#666',
                    '&:hover': {
                      bgcolor: hasAsset ? '#00897b' : 'var(--rs-paper)'
                    },
                    '&:disabled': {
                      color: '#3a4456'
                    }
                  }}
                  onClick={() => hasAsset && playCart(item.asset_id, pos, duration)}
                >
                  <Box sx={{ textAlign: 'left', width: '100%', overflow: 'hidden' }}>
                    <Typography variant="body2" noWrap>
                      {item ? (item.title || 'Untitled') : '(empty)'}
                    </Typography>
                    <Typography variant="caption" color="inherit" sx={{ opacity: 0.8 }} noWrap>
                      {item ? (item.artist || 'Cart') + (duration ? ` • ${formatDuration(duration)}` : '') : ''}
                    </Typography>
                    {isPlaying && playingRemaining > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600, color: '#22d3ee' }}>
                        {formatDuration(playingRemaining)} remaining
                      </Typography>
                    )}
                  </Box>
                </Button>
                {hasAsset && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Headphones />}
                    disabled={!supportsSinkId || !selectedPreviewOutput}
                    onClick={() => handlePreviewClick(item.asset_id, pos, duration)}
                    sx={{
                      py: 0.5,
                      borderColor: previewPlayingPosition === pos ? '#f44336' : '#22d3ee',
                      color: previewPlayingPosition === pos ? '#f44336' : '#22d3ee',
                      '&:hover': {
                        borderColor: previewPlayingPosition === pos ? '#f44336' : '#22d3ee',
                        bgcolor: previewPlayingPosition === pos ? 'rgba(244, 67, 54, 0.1)' : 'rgba(0, 212, 255, 0.1)'
                      },
                      '&.Mui-disabled': { borderColor: '#3a4456', color: '#5b6675' }
                    }}
                  >
                    {previewPlayingPosition === pos ? 'Stop' : 'Preview'}
                  </Button>
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        open={libraryDialogOpen}
        onClose={() => { setLibraryDialogOpen(false); setCartSearch(''); setSelectedCategory(''); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' } }}
      >
        <DialogTitle sx={{ color: '#22d3ee', borderBottom: '1px solid var(--rs-border)' }}>
          Add to Cartwall – Select from Library
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Search library"
              value={cartSearch}
              onChange={(e) => setCartSearch(e.target.value)}
              sx={{ input: { color: 'var(--rs-text)' } }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel sx={{ color: '#999' }}>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
                sx={{ color: 'var(--rs-text)' }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Grid container spacing={1} sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {libraryLoading ? (
              <Grid item xs={12} sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Loading library…</Typography>
              </Grid>
            ) : filteredAssets.length === 0 ? (
              <Grid item xs={12} sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {allAssets.length === 0 ? 'No assets in library. Upload some first.' : 'No matches for this search or category.'}
                </Typography>
              </Grid>
            ) : filteredAssets.map((asset) => (
              <Grid item xs={12} sm={6} md={4} key={asset.id}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    justifyContent: 'flex-start',
                    borderColor: '#22d3ee',
                    color: '#22d3ee',
                    '&:hover': { borderColor: '#22d3ee', bgcolor: 'rgba(0, 212, 255, 0.1)' }
                  }}
                  onClick={() => addAssetToCartwall(asset)}
                >
                  <Box sx={{ textAlign: 'left', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>{asset.title}</Typography>
                      {(asset.categories || []).slice(0, 2).map((cat) => (
                        <Chip key={cat.id} label={cat.name} size="small" sx={{ bgcolor: cat.color || '#666', color: '#fff', fontSize: '0.65rem', height: 18 }} />
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ opacity: 0.8 }} noWrap>
                      {asset.artist || 'Unknown'} • {formatDuration(asset.duration || 0)}
                    </Typography>
                  </Box>
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
